import { useTranslation } from 'react-i18next'

import { RENTAL_PORTAL_APPLICANTS_SINGLE_FIELD_API } from '../../../constants/api'
import { NOTIFICATION_TYPE } from '../../../constants/common'
import { RENTAL_APPLICANT_ENTITY } from '../../../constants/entity'
import { MENU_RENTAL_PORTAL_ITEM } from '../../../constants/menu'
import {
  ROUTE_PATH_LIST_RENTAL_PORTAL,
  SUBMENU_DEFAULT_VALUE,
} from '../../../constants/route'
import { ScreenContainerRef } from '../../../context'
import { usePageBrowserTabTitle } from '../../../hooks'
import { FilterBar } from '../../Filter'
import { NotificationInline } from '../../Notification'
import { Page } from '../../Page'
import { PageHeader } from '../../PageHeader'
import { TablePagination } from '../../TablePagination'

import {
  APPLICANTS_INITIAL_RANGE_FILTER_NAMES,
  QA_NAME_APPLICANTS_PAGE,
  QA_NAME_APPLICANTS_REFRESH_ERROR,
} from './RentalPortalApplicants.constants'
import { useRentalPortalApplicantsTablePage } from './RentalPortalApplicants.hooks'
import { getApplicantsRefreshErrorText } from './RentalPortalApplicants.utils'
import { RentalPortalApplicantsTableLayout } from './RentalPortalApplicantsTableLayout'

import * as styles from './RentalPortalApplicants.module.css'

import RefreshIcon from '../../../icons/refresh.svg'

function RentalPortalApplicants() {
  const { t } = useTranslation()

  const tablePage = useRentalPortalApplicantsTablePage()

  const {
    applicants,
    availableFilters,
    filterValues,
    handleReload,
    handleSetFilterValues,
    handleSetPagination,
    hasActiveFilters,
    isBoardRegime,
    isLoading,
    isRefetching,
    ordering,
    pageScrollWrapperRef,
    pagination,
    permissions,
    serverError,
  } = tablePage

  usePageBrowserTabTitle({
    isLoading,
    pageTitle: t('RentalPortal.ApplicantsTitleKey'),
  })

  const hasApplicants = applicants.length > 0

  const hasBodyError = Boolean(serverError) && !hasApplicants

  const hasStaleError = Boolean(serverError) && hasApplicants

  const shouldShowFilterBar =
    hasApplicants || hasActiveFilters || isLoading || hasBodyError

  const breadcrumbs = [
    {
      id: MENU_RENTAL_PORTAL_ITEM.title,
      title: t(MENU_RENTAL_PORTAL_ITEM.title),
      to: `/${ROUTE_PATH_LIST_RENTAL_PORTAL.root}`,
    },
    {
      id: 'RentalPortal.ApplicantsTitleKey',
      title: t('RentalPortal.ApplicantsTitleKey'),
    },
  ]

  return (
    <ScreenContainerRef value={pageScrollWrapperRef}>
      <Page
        pagination={
          <TablePagination
            responsive
            disabled={isBoardRegime}
            isSkeleton={isLoading}
            value={pagination}
            onChange={handleSetPagination}
          />
        }
        qaName={QA_NAME_APPLICANTS_PAGE}
        scrollWrapperRef={pageScrollWrapperRef}
      >
        <PageHeader
          breadcrumbs={breadcrumbs}
          isLoading={isLoading}
          itemsCount={pagination.count}
          title={t('RentalPortal.ApplicantsTitleKey')}
        />
        {shouldShowFilterBar ? (
          <FilterBar
            hideFilterTemplateSkeleton
            responsive
            apiUrl={RENTAL_PORTAL_APPLICANTS_SINGLE_FIELD_API}
            availableFilters={availableFilters}
            canViewFilterTemplate={permissions?.can_view_filter_template}
            entity={RENTAL_APPLICANT_ENTITY}
            filterValues={filterValues}
            initialRangeFilterNames={APPLICANTS_INITIAL_RANGE_FILTER_NAMES}
            isSkeleton={isLoading}
            ordering={ordering}
            pagination={{ per_page: pagination.pageSize }}
            setFilterValues={handleSetFilterValues}
            sub={SUBMENU_DEFAULT_VALUE}
            onReload={handleReload}
          />
        ) : null}
        {hasStaleError ? (
          <NotificationInline
            actions={[
              {
                beforeIconProps: { Icon: RefreshIcon },
                callback: handleReload,
                isLoading: isRefetching,
                text: t('RentalPortal.ListingRetryBtnKey'),
              },
            ]}
            className={styles.refreshError}
            qaName={QA_NAME_APPLICANTS_REFRESH_ERROR}
            text={getApplicantsRefreshErrorText({
              availableFilters,
              filterValues,
              t,
            })}
            type={NOTIFICATION_TYPE.error}
          />
        ) : null}
        <RentalPortalApplicantsTableLayout
          hasBodyError={hasBodyError}
          tablePage={tablePage}
        />
      </Page>
    </ScreenContainerRef>
  )
}

export default RentalPortalApplicants
